import React, { useState, useEffect, useCallback } from 'react';
import { showToast } from '../utils/toast';
import { FiEdit2, FiPlus, FiToggleLeft, FiToggleRight, FiSearch } from 'react-icons/fi';
import api from '../services/api';
import '../styles/Maestros.css';

const EMPTY = {
  ruc: '', nombre: '', representante: '', nro_cuenta: '',
  direccion: '', telefono: '', celular: '', web: '', email: '',
};

export default function ProveedoresPage() {
  const [rows,       setRows]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [search,     setSearch]     = useState('');
  const [modal,      setModal]      = useState(null);
  const [form,       setForm]       = useState(EMPTY);
  const [saving,     setSaving]     = useState(false);
  const [modalError, setModalError] = useState('');

  const load = useCallback(async (q = '') => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/api/proveedores', { params: { q } });
      setRows(r.data.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => {
    const v = e.target.value;
    setSearch(v);
    load(v);
  };

  const openNew = () => {
    setForm(EMPTY);
    setModalError('');
    setModal({ mode: 'new' });
  };

  const openEdit = (r) => {
    setForm({
      ruc:          r.ruc          || '',
      nombre:       r.nombre       || '',
      representante: r.representante || '',
      nro_cuenta:   r.nro_cuenta   || '',
      direccion:    r.direccion    || '',
      telefono:     r.telefono     || '',
      celular:      r.celular      || '',
      web:          r.web          || '',
      email:        r.email        || '',
    });
    setModalError('');
    setModal({ mode: 'edit', id: r.id });
  };

  const handleChange = (field, val) => {
    setModalError('');
    setForm(p => ({ ...p, [field]: val }));
  };

  const PHONE = /^[\d\s+\-()\\.]{6,20}$/;

  const validate = () => {
    if (!form.nombre.trim())
      return 'Nombre / Razón Social es requerido.';
    if (form.nombre.trim().length > 150)
      return 'Nombre no puede exceder 150 caracteres.';

    const ruc = form.ruc.trim();
    if (ruc && !/^\d{11}$/.test(ruc))
      return 'RUC debe tener exactamente 11 dígitos numéricos.';

    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      return 'Formato de email inválido.';
    if (form.telefono.trim() && !PHONE.test(form.telefono.trim()))
      return 'Teléfono inválido. Use dígitos, +, -, espacios (6-20 caracteres).';
    if (form.celular.trim() && !PHONE.test(form.celular.trim()))
      return 'Celular inválido. Use dígitos, +, -, espacios (6-20 caracteres).';
    if (form.web.trim() && !/\S+\.\S+/.test(form.web.trim()))
      return 'Web inválida. Ejemplo: www.empresa.com';

    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setModalError(err); return; }
    setSaving(true); setModalError('');
    try {
      if (modal.mode === 'new') {
        await api.post('/api/proveedores', form);
      } else {
        await api.put(`/api/proveedores/${modal.id}`, form);
      }
      await load(search);
      setModal(null);
    } catch (e) {
      setModalError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await api.patch(`/api/proveedores/${id}/activo`);
      await load(search);
    } catch (e) {
      showToast(e.response?.data?.error || 'Error');
    }
  };

  return (
    <div className="mst-page">
      {/* Toolbar */}
      <div className="mst-toolbar">
        <button className="mst-btn-nuevo" onClick={openNew}>
          <FiPlus size={14} /> Nuevo Proveedor
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FiSearch size={13} style={{ color: '#94a3b8' }} />
          <input
            className="mst-search"
            placeholder="Buscar por nombre, RUC, teléfono..."
            value={search}
            onChange={handleSearch}
          />
        </div>
        <span className="mst-count">{rows.length} registro(s)</span>
      </div>

      {/* Tabla */}
      <div className="mst-table-wrap">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#64748b' }}>Cargando...</div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#dc2626' }}>{error}</div>
        ) : (
          <table className="mst-table">
            <thead>
              <tr>
                <th className="mst-th" style={{ width: 120 }}>RUC</th>
                <th className="mst-th">Proveedor</th>
                <th className="mst-th" style={{ width: 160 }}>Representante</th>
                <th className="mst-th" style={{ width: 110 }}>Teléfono</th>
                <th className="mst-th" style={{ width: 100 }}>Celular</th>
                <th className="mst-th">Dirección</th>
                <th className="mst-th mst-th-center" style={{ width: 80 }}>Estado</th>
                <th className="mst-th mst-th-center" style={{ width: 80 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={8} className="mst-empty">Sin proveedores registrados</td></tr>
              ) : rows.map(r => (
                <tr key={r.id}>
                  <td className="mst-td" style={{ fontFamily: 'monospace' }}>{r.ruc || '—'}</td>
                  <td className="mst-td" style={{ fontWeight: 600 }}>{r.nombre}</td>
                  <td className="mst-td">{r.representante || '—'}</td>
                  <td className="mst-td">{r.telefono || '—'}</td>
                  <td className="mst-td">{r.celular || '—'}</td>
                  <td className="mst-td" style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.direccion || '—'}
                  </td>
                  <td className="mst-td mst-td-center">
                    <span className={`mst-badge ${r.activo ? 'mst-badge-active' : 'mst-badge-inactive'}`}>
                      {r.activo ? 'ACTIVO' : 'INACTIVO'}
                    </span>
                  </td>
                  <td className="mst-td">
                    <div className="ia-actions">
                      <button className="ia-btn ia-btn--edit" title="Editar" onClick={() => openEdit(r)}>
                        <FiEdit2 size={15} />
                      </button>
                      <button
                        className={`ia-btn ${r.activo ? 'ia-btn--on' : 'ia-btn--off'}`}
                        title={r.activo ? 'Desactivar' : 'Activar'}
                        onClick={() => handleToggle(r.id)}
                      >
                        {r.activo ? <FiToggleLeft size={15} /> : <FiToggleRight size={15} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mst-footer">
        Todos los Derechos reservados Devel@per F@xy &nbsp;·&nbsp; www.developerfoxy.com
      </div>

      {/* Modal */}
      {modal && (
        <div className="mst-overlay">
          <div className="mst-modal mst-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="mst-modal-header">
              <span>{modal.mode === 'new' ? 'Nuevo Proveedor' : 'Editar Proveedor'}</span>
              <button className="mst-modal-close" onClick={() => setModal(null)}>×</button>
            </div>

            <div className="mst-modal-body">
              <div className="mst-row mst-row-2">
                <div className="mst-field">
                  <label className="mst-label">RUC</label>
                  <input className="mst-input" value={form.ruc}
                    onChange={e => handleChange('ruc', e.target.value.replace(/\D/g, ''))}
                    placeholder="11 dígitos" maxLength={11} />
                </div>
                <div className="mst-field">
                  <label className="mst-label">Nombre / Razón Social *</label>
                  <input className="mst-input" value={form.nombre}
                    onChange={e => handleChange('nombre', e.target.value)}
                    placeholder="Nombre o razón social" autoFocus />
                </div>
              </div>

              <div className="mst-row mst-row-2">
                <div className="mst-field">
                  <label className="mst-label">Representante / Contacto</label>
                  <input className="mst-input" value={form.representante}
                    onChange={e => handleChange('representante', e.target.value)}
                    placeholder="Nombre del contacto" />
                </div>
                <div className="mst-field">
                  <label className="mst-label">N° Cuenta Bancaria</label>
                  <input className="mst-input" value={form.nro_cuenta}
                    onChange={e => handleChange('nro_cuenta', e.target.value)}
                    placeholder="Número de cuenta" />
                </div>
              </div>

              <div className="mst-row mst-row-2">
                <div className="mst-field">
                  <label className="mst-label">Teléfono</label>
                  <input className="mst-input" value={form.telefono}
                    onChange={e => handleChange('telefono', e.target.value)}
                    placeholder="Teléfono fijo" />
                </div>
                <div className="mst-field">
                  <label className="mst-label">Celular</label>
                  <input className="mst-input" value={form.celular}
                    onChange={e => handleChange('celular', e.target.value)}
                    placeholder="999 999 999" />
                </div>
              </div>

              <div className="mst-row mst-row-2">
                <div className="mst-field">
                  <label className="mst-label">Email</label>
                  <input className="mst-input" type="email" value={form.email}
                    onChange={e => handleChange('email', e.target.value)}
                    placeholder="correo@proveedor.com" />
                </div>
                <div className="mst-field">
                  <label className="mst-label">Sitio Web</label>
                  <input className="mst-input" value={form.web}
                    onChange={e => handleChange('web', e.target.value)}
                    placeholder="www.proveedor.com" />
                </div>
              </div>

              <div className="mst-row mst-row-1">
                <div className="mst-field">
                  <label className="mst-label">Dirección</label>
                  <input className="mst-input" value={form.direccion}
                    onChange={e => handleChange('direccion', e.target.value)}
                    placeholder="Dirección completa" />
                </div>
              </div>
            </div>

            {modalError && <div className="mst-modal-error">{modalError}</div>}

            <div className="mst-modal-footer">
              <button className="mst-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : modal.mode === 'new' ? 'Guardar' : 'Actualizar'}
              </button>
              <button className="mst-btn-cancel" onClick={() => setModal(null)}>× Salir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
